import React, { useState, useEffect } from 'react';
import { Modal, Button, Row, Col, Card, Badge, Spinner, Alert, ProgressBar, Tab, Tabs } from 'react-bootstrap';
import useServerHealth from '../../hooks/useServerHealth';
import './ServerHealthMonitor.css';

const ServerHealthMonitor = () => {
  const [show, setShow] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  const { 
    metrics, 
    dbActivity, 
    loading, 
    error, 
    lastUpdated, 
    refresh, 
    fetchMetrics 
  } = useServerHealth(show && autoRefresh, 5000);

  const handleClose = () => setShow(false);
  const handleShow = () => {
    setShow(true);
    if (!metrics) {
      fetchMetrics();
    }
  };

  const resetMetrics = async () => {
    try {
      await fetch('/api/health/reset-metrics', { method: 'POST' });
      refresh(); // Refresh after reset
    } catch (error) {
      console.error('Error resetting metrics:', error);
    }
  };

  const formatUptime = (seconds) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getHealthBadgeVariant = (status) => {
    switch (status) {
      case 'healthy': return 'success';
      case 'warning': return 'warning';
      case 'critical': return 'danger';
      default: return 'secondary';
    }
  };

  const getHealthScoreVariant = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'danger';
  };

  const getMemoryVariant = (percentage) => {
    if (percentage < 70) return 'success';
    if (percentage < 85) return 'warning';
    return 'danger';
  };

  const getConnectionVariant = (percentage) => {
    if (percentage < 60) return 'success';
    if (percentage < 80) return 'warning';
    return 'danger';
  };

  const getDiskVariant = (percentage) => {
    if (percentage < 70) return 'success';
    if (percentage < 85) return 'warning';
    return 'danger';
  };

  const getSeverityVariant = (severity) => {
    switch (severity) {
      case 'low': return 'info';
      case 'medium': return 'warning';
      case 'high': return 'warning';
      case 'critical': return 'danger';
      default: return 'secondary';
    }
  };

  const getResponsivenessVariant = (responseTime) => {
    if (responseTime < 1000) return 'success';
    if (responseTime < 2000) return 'warning';
    return 'danger';
  };

  const getResponsivenessHealthVariant = (isHealthy) => {
    return isHealthy ? 'success' : 'danger';
  };

  return (
    <>
      {/* Floating Button */}
      <div className="floating-health-button" onClick={handleShow}>
        <i className="bi bi-activity"></i>
        <span className="health-tooltip">Server Health</span>
        {metrics?.health?.score && (
          <div className="health-score-indicator">
            <Badge bg={getHealthScoreVariant(metrics.health.score)} className="score-badge">
              {metrics.health.score}
            </Badge>
          </div>
        )}
      </div>

      {/* Health Monitoring Modal */}
      <Modal show={show} onHide={handleClose} size="xl" className="health-monitor-modal">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-activity me-2"></i>
            Server Health Monitor
            {metrics && (
              <>
                <Badge bg={getHealthBadgeVariant(metrics.health.status)} className="ms-2">
                  {metrics.health.status.toUpperCase()}
                </Badge>
                <Badge bg={getHealthScoreVariant(metrics.health.score)} className="ms-2">
                  Score: {metrics.health.score}/100
                </Badge>
              </>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={refresh}
                disabled={loading}
                className="me-2"
              >
                {loading ? <Spinner size="sm" /> : <i className="bi bi-arrow-clockwise"></i>}
                Refresh
              </Button>
              <Button
                variant={autoRefresh ? "success" : "outline-secondary"}
                size="sm"
                onClick={() => setAutoRefresh(!autoRefresh)}
                className="me-2"
              >
                <i className="bi bi-arrow-repeat"></i>
                Auto Refresh
              </Button>
              <Button
                variant="outline-warning"
                size="sm"
                onClick={resetMetrics}
                disabled={loading}
              >
                <i className="bi bi-arrow-counterclockwise"></i>
                Reset Metrics
              </Button>
            </div>
            {metrics && (
              <small className="text-muted">
                Last updated: {lastUpdated ? lastUpdated.toLocaleTimeString() : 'Never'}
              </small>
            )}
          </div>

          {error && (
            <Alert variant="danger">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          )}

          {loading && !metrics && (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-3">Loading health metrics...</p>
            </div>
          )}

          {metrics && (
            <>
              {/* Health Issues */}
              {metrics.health.issues.length > 0 && (
                <Alert variant="warning">
                  <Alert.Heading>
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    Health Issues Detected ({metrics.health.issues.length})
                  </Alert.Heading>
                  <div className="health-issues">
                    {metrics.health.issues.map((issue, index) => (
                      <div key={index} className="d-flex justify-content-between align-items-center mb-2">
                        <span>{issue.message}</span>
                        <Badge bg={getSeverityVariant(issue.severity)}>
                          {issue.severity}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </Alert>
              )}

              <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-3">
                {/* Overview Tab */}
                <Tab eventKey="overview" title="Overview">
                  <Row className="g-4">
                    {/* Health Score */}
                    <Col md={12}>
                      <Card>
                        <Card.Header>
                          <i className="bi bi-heart-pulse me-2"></i>
                          Health Overview
                        </Card.Header>
                        <Card.Body>
                          <Row>
                            <Col md={3}>
                              <div className="text-center">
                                <div className="health-score-circle">
                                  <span className={`score-text text-${getHealthScoreVariant(metrics.health.score)}`}>
                                    {metrics.health.score}
                                  </span>
                                  <small>/ 100</small>
                                </div>
                                <p className="mt-2 mb-0">Health Score</p>
                              </div>
                            </Col>
                            <Col md={9}>
                              <div className="metric-item">
                                <strong>Status:</strong> 
                                <Badge bg={getHealthBadgeVariant(metrics.health.status)} className="ms-2">
                                  {metrics.health.status.toUpperCase()}
                                </Badge>
                              </div>
                              <div className="metric-item">
                                <strong>Issues:</strong> {metrics.health.issues.length}
                              </div>
                              <div className="metric-item">
                                <strong>Uptime:</strong> {formatUptime(metrics.server.uptime)}
                              </div>
                              <div className="metric-item">
                                <strong>Hostname:</strong> {metrics.server.hostname}
                              </div>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    </Col>

                    {/* Memory Usage */}
                    <Col md={6}>
                      <Card>
                        <Card.Header>
                          <i className="bi bi-memory me-2"></i>
                          Memory Usage
                        </Card.Header>
                        <Card.Body>
                          <div className="mb-3">
                            <div className="d-flex justify-content-between">
                              <span>Process Memory:</span>
                              <span>{metrics.memory.process.heapUsed}MB / {metrics.memory.process.heapTotal}MB</span>
                            </div>
                            <ProgressBar 
                              now={(metrics.memory.process.heapUsed / metrics.memory.process.heapTotal) * 100}
                              variant={getMemoryVariant((metrics.memory.process.heapUsed / metrics.memory.process.heapTotal) * 100)}
                            />
                          </div>
                          <div className="mb-3">
                            <div className="d-flex justify-content-between">
                              <span>System Memory:</span>
                              <span>{metrics.memory.system.used}MB / {metrics.memory.system.total}MB</span>
                            </div>
                            <ProgressBar 
                              now={metrics.memory.system.percentage}
                              variant={getMemoryVariant(metrics.memory.system.percentage)}
                            />
                          </div>
                          <div className="metric-item">
                            <strong>RSS:</strong> {metrics.memory.process.rss}MB
                          </div>
                          <div className="metric-item">
                            <strong>External:</strong> {metrics.memory.process.external}MB
                          </div>
                          <div className="metric-item">
                            <strong>Array Buffers:</strong> {metrics.memory.process.arrayBuffers}MB
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>

                    {/* Database Connections */}
                    <Col md={6}>
                      <Card>
                        <Card.Header>
                          <i className="bi bi-database me-2"></i>
                          Database Connections
                        </Card.Header>
                        <Card.Body>
                          <div className="mb-3">
                            <div className="d-flex justify-content-between">
                              <span>Connection Pool:</span>
                              <span>{metrics.database.total_connections} / {metrics.database.max_connections}</span>
                            </div>
                            <ProgressBar 
                              now={metrics.database.connectionPoolHealth}
                              variant={getConnectionVariant(metrics.database.connectionPoolHealth)}
                            />
                          </div>
                          <div className="metric-item">
                            <strong>Active:</strong> {metrics.database.active_connections}
                          </div>
                          <div className="metric-item">
                            <strong>Idle:</strong> {metrics.database.idle_connections}
                          </div>
                          <div className="metric-item">
                            <strong>Idle in Transaction:</strong> {metrics.database.idle_in_transaction}
                          </div>
                          <div className="metric-item">
                            <strong>Current Active:</strong> {metrics.database.currentActive}
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>

                    {/* Performance Metrics */}
                    <Col md={6}>
                      <Card>
                        <Card.Header>
                          <i className="bi bi-speedometer2 me-2"></i>
                          Performance Metrics
                        </Card.Header>
                        <Card.Body>
                          <div className="metric-item">
                            <strong>Requests:</strong> {metrics.performance.requestCount}
                          </div>
                          <div className="metric-item">
                            <strong>Request Rate:</strong> {metrics.performance.requestRate} req/sec
                          </div>
                          <div className="metric-item">
                            <strong>Avg Response Time:</strong> {metrics.performance.avgResponseTime}ms
                          </div>
                          <div className="metric-item">
                            <strong>Error Rate:</strong> 
                            <Badge bg={metrics.performance.errorRate > 5 ? 'danger' : 'success'} className="ms-2">
                              {metrics.performance.errorRate}%
                            </Badge>
                          </div>
                          <div className="metric-item">
                            <strong>Event Loop Delay:</strong> {metrics.performance.eventLoopDelay}ms
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>

                    {/* Server Responsiveness */}
                    <Col md={6}>
                      <Card>
                        <Card.Header>
                          <i className="bi bi-lightning me-2"></i>
                          Server Responsiveness
                        </Card.Header>
                        <Card.Body>
                          <div className="metric-item">
                            <strong>Status:</strong> 
                            <Badge bg={getResponsivenessHealthVariant(metrics.responsiveness?.isHealthy)} className="ms-2">
                              {metrics.responsiveness?.isHealthy ? 'HEALTHY' : 'DEGRADED'}
                            </Badge>
                          </div>
                          <div className="metric-item">
                            <strong>Recent Requests (1min):</strong> {metrics.responsiveness?.recentRequests || 0}
                          </div>
                          <div className="metric-item">
                            <strong>Avg Response Time:</strong> 
                            <Badge bg={getResponsivenessVariant(metrics.responsiveness?.averageResponseTime || 0)} className="ms-2">
                              {metrics.responsiveness?.averageResponseTime || 0}ms
                            </Badge>
                          </div>
                          <div className="metric-item">
                            <strong>Slow Requests:</strong> 
                            <Badge bg={metrics.responsiveness?.slowRequests > 5 ? 'warning' : 'success'} className="ms-2">
                              {metrics.responsiveness?.slowRequests || 0}
                            </Badge>
                          </div>
                          <div className="metric-item">
                            <strong>Critical Requests:</strong> 
                            <Badge bg={metrics.responsiveness?.criticalRequests > 0 ? 'danger' : 'success'} className="ms-2">
                              {metrics.responsiveness?.criticalRequests || 0}
                            </Badge>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>

                    {/* Disk Usage */}
                    <Col md={6}>
                      <Card>
                        <Card.Header>
                          <i className="bi bi-hdd me-2"></i>
                          Disk Usage
                        </Card.Header>
                        <Card.Body>
                          <div className="mb-3">
                            <div className="d-flex justify-content-between">
                              <span>Disk Space:</span>
                              <span>{metrics.disk.used}GB / {metrics.disk.total}GB</span>
                            </div>
                            <ProgressBar 
                              now={metrics.disk.percentage}
                              variant={getDiskVariant(metrics.disk.percentage)}
                            />
                          </div>
                          <div className="metric-item">
                            <strong>Available:</strong> {metrics.disk.available}GB
                          </div>
                          <div className="metric-item">
                            <strong>Usage:</strong> 
                            <Badge bg={getDiskVariant(metrics.disk.percentage)} className="ms-2">
                              {metrics.disk.percentage}%
                            </Badge>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Tab>

                {/* Responsiveness Tab */}
                <Tab eventKey="responsiveness" title="Responsiveness">
                  <Row className="g-4">
                    {/* Responsiveness Overview */}
                    <Col md={6}>
                      <Card>
                        <Card.Header>
                          <i className="bi bi-lightning me-2"></i>
                          Response Time Analysis
                        </Card.Header>
                        <Card.Body>
                          <div className="metric-item">
                            <strong>Overall Status:</strong> 
                            <Badge bg={getResponsivenessHealthVariant(metrics.responsiveness?.isHealthy)} className="ms-2">
                              {metrics.responsiveness?.isHealthy ? 'HEALTHY' : 'DEGRADED'}
                            </Badge>
                          </div>
                          <div className="metric-item">
                            <strong>Average Response Time:</strong>
                            <Badge bg={getResponsivenessVariant(metrics.responsiveness?.averageResponseTime || 0)} className="ms-2">
                              {metrics.responsiveness?.averageResponseTime || 0}ms
                            </Badge>
                          </div>
                          <div className="metric-item">
                            <strong>Recent Requests (1 min):</strong> {metrics.responsiveness?.recentRequests || 0}
                          </div>
                          
                          {/* Response Time Categories */}
                          <div className="mt-3">
                            <h6>Request Categories (Last Minute)</h6>
                            <div className="metric-item">
                              <strong>Fast Requests (&lt;1s):</strong>
                              <Badge bg="success" className="ms-2">
                                {Math.max(0, (metrics.responsiveness?.recentRequests || 0) - (metrics.responsiveness?.slowRequests || 0) - (metrics.responsiveness?.criticalRequests || 0))}
                              </Badge>
                            </div>
                            <div className="metric-item">
                              <strong>Slow Requests (1-15s):</strong>
                              <Badge bg={metrics.responsiveness?.slowRequests > 5 ? 'warning' : 'success'} className="ms-2">
                                {metrics.responsiveness?.slowRequests || 0}
                              </Badge>
                            </div>
                            <div className="metric-item">
                              <strong>Critical Requests (&gt;15s):</strong>
                              <Badge bg={metrics.responsiveness?.criticalRequests > 0 ? 'danger' : 'success'} className="ms-2">
                                {metrics.responsiveness?.criticalRequests || 0}
                              </Badge>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>

                    {/* Responsiveness Health Indicators */}
                    <Col md={6}>
                      <Card>
                        <Card.Header>
                          <i className="bi bi-graph-up me-2"></i>
                          Health Indicators
                        </Card.Header>
                        <Card.Body>
                          {/* Health Score Impact */}
                          <div className="metric-item">
                            <strong>Impact on Health Score:</strong>
                            {!metrics.responsiveness?.isHealthy ? (
                              <Badge bg="warning" className="ms-2">
                                Negatively Affecting
                              </Badge>
                            ) : (
                              <Badge bg="success" className="ms-2">
                                No Impact
                              </Badge>
                            )}
                          </div>

                          {/* Response Time Thresholds */}
                          <div className="mt-3">
                            <h6>Threshold Information</h6>
                            <div className="metric-item text-success">
                              <i className="bi bi-check-circle me-2"></i>
                              <strong>Fast:</strong> &lt; 1,000ms
                            </div>
                            <div className="metric-item text-warning">
                              <i className="bi bi-exclamation-triangle me-2"></i>
                              <strong>Slow:</strong> 1,000ms - 15,000ms
                            </div>
                            <div className="metric-item text-danger">
                              <i className="bi bi-x-circle me-2"></i>
                              <strong>Critical:</strong> &gt; 15,000ms
                            </div>
                          </div>

                          {/* Responsiveness Recommendations */}
                          <div className="mt-3">
                            <h6>Recommendations</h6>
                            {!metrics.responsiveness?.isHealthy ? (
                              <Alert variant="warning" className="mb-2 py-2">
                                <small>
                                  <i className="bi bi-lightbulb me-2"></i>
                                  Consider checking for stuck sync operations or database issues.
                                </small>
                              </Alert>
                            ) : (
                              <Alert variant="success" className="mb-2 py-2">
                                <small>
                                  <i className="bi bi-check-circle me-2"></i>
                                  Server is responding well within acceptable thresholds.
                                </small>
                              </Alert>
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>

                    {/* Response Time Distribution */}
                    <Col md={12}>
                      <Card>
                        <Card.Header>
                          <i className="bi bi-bar-chart me-2"></i>
                          Response Time Distribution (Last Minute)
                        </Card.Header>
                        <Card.Body>
                          {metrics.responsiveness?.recentRequests > 0 ? (
                            <Row>
                              <Col md={4}>
                                <div className="text-center">
                                  <div className="metric-circle bg-success text-white d-inline-flex align-items-center justify-content-center rounded-circle" style={{width: '80px', height: '80px'}}>
                                    <span className="fw-bold">
                                      {Math.max(0, (metrics.responsiveness?.recentRequests || 0) - (metrics.responsiveness?.slowRequests || 0) - (metrics.responsiveness?.criticalRequests || 0))}
                                    </span>
                                  </div>
                                  <p className="mt-2 mb-0">Fast Requests</p>
                                  <small className="text-muted">&lt; 1 second</small>
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className="text-center">
                                  <div className={`metric-circle text-white d-inline-flex align-items-center justify-content-center rounded-circle ${metrics.responsiveness?.slowRequests > 5 ? 'bg-warning' : 'bg-success'}`} style={{width: '80px', height: '80px'}}>
                                    <span className="fw-bold">
                                      {metrics.responsiveness?.slowRequests || 0}
                                    </span>
                                  </div>
                                  <p className="mt-2 mb-0">Slow Requests</p>
                                  <small className="text-muted">1-15 seconds</small>
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className="text-center">
                                  <div className={`metric-circle text-white d-inline-flex align-items-center justify-content-center rounded-circle ${metrics.responsiveness?.criticalRequests > 0 ? 'bg-danger' : 'bg-success'}`} style={{width: '80px', height: '80px'}}>
                                    <span className="fw-bold">
                                      {metrics.responsiveness?.criticalRequests || 0}
                                    </span>
                                  </div>
                                  <p className="mt-2 mb-0">Critical Requests</p>
                                  <small className="text-muted">&gt; 15 seconds</small>
                                </div>
                              </Col>
                            </Row>
                          ) : (
                            <Alert variant="info">
                              <i className="bi bi-info-circle me-2"></i>
                              No recent requests to analyze. Start using the application to see responsiveness metrics.
                            </Alert>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Tab>

                {/* System Info Tab */}
                <Tab eventKey="system" title="System Info">
                  <Row className="g-4">
                    {/* Server Info */}
                    <Col md={6}>
                      <Card>
                        <Card.Header>
                          <i className="bi bi-server me-2"></i>
                          Server Information
                        </Card.Header>
                        <Card.Body>
                          <div className="metric-item">
                            <strong>Hostname:</strong> {metrics.server.hostname}
                          </div>
                          <div className="metric-item">
                            <strong>Platform:</strong> {metrics.server.platform} ({metrics.server.arch})
                          </div>
                          <div className="metric-item">
                            <strong>OS Type:</strong> {metrics.server.type}
                          </div>
                          <div className="metric-item">
                            <strong>OS Release:</strong> {metrics.server.release}
                          </div>
                          <div className="metric-item">
                            <strong>Node Version:</strong> {metrics.server.nodeVersion}
                          </div>
                          <div className="metric-item">
                            <strong>User:</strong> {metrics.server.userInfo.username}
                          </div>
                          <div className="metric-item">
                            <strong>Home Dir:</strong> {metrics.server.homedir}
                          </div>
                          <div className="metric-item">
                            <strong>Temp Dir:</strong> {metrics.server.tmpdir}
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>

                    {/* Process Info */}
                    <Col md={6}>
                      <Card>
                        <Card.Header>
                          <i className="bi bi-cpu me-2"></i>
                          Process Information
                        </Card.Header>
                        <Card.Body>
                          <div className="metric-item">
                            <strong>PID:</strong> {metrics.process.pid}
                          </div>
                          <div className="metric-item">
                            <strong>PPID:</strong> {metrics.process.ppid}
                          </div>
                          <div className="metric-item">
                            <strong>Title:</strong> {metrics.process.title}
                          </div>
                          <div className="metric-item">
                            <strong>Exec Path:</strong> 
                            <code className="ms-2 small">{metrics.process.execPath}</code>
                          </div>
                          <div className="metric-item">
                            <strong>CWD:</strong> 
                            <code className="ms-2 small">{metrics.process.cwd}</code>
                          </div>
                          <div className="metric-item">
                            <strong>File Descriptors:</strong> {metrics.process.fileDescriptors}
                          </div>
                          <div className="metric-item">
                            <strong>Active Handles:</strong> {metrics.process.activeHandles}
                          </div>
                          <div className="metric-item">
                            <strong>Active Requests:</strong> {metrics.process.activeRequests}
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>

                    {/* CPU Info */}
                    <Col md={6}>
                      <Card>
                        <Card.Header>
                          <i className="bi bi-cpu me-2"></i>
                          CPU Information
                        </Card.Header>
                        <Card.Body>
                          <div className="metric-item">
                            <strong>Cores:</strong> {metrics.cpu.cores}
                          </div>
                          <div className="metric-item">
                            <strong>Model:</strong> {metrics.cpu.model}
                          </div>
                          <div className="metric-item">
                            <strong>Speed:</strong> {metrics.cpu.speed} MHz
                          </div>
                          <div className="metric-item">
                            <strong>Usage:</strong> 
                            <Badge bg={metrics.cpu.usage > 80 ? 'danger' : 'success'} className="ms-2">
                              {metrics.cpu.usage}%
                            </Badge>
                          </div>
                          <div className="metric-item">
                            <strong>Load Avg (1m):</strong> {metrics.cpu.loadAverage['1min']}
                          </div>
                          <div className="metric-item">
                            <strong>Load Avg (5m):</strong> {metrics.cpu.loadAverage['5min']}
                          </div>
                          <div className="metric-item">
                            <strong>Load Avg (15m):</strong> {metrics.cpu.loadAverage['15min']}
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>

                    {/* Network Interfaces */}
                    <Col md={6}>
                      <Card>
                        <Card.Header>
                          <i className="bi bi-wifi me-2"></i>
                          Network Interfaces ({metrics.network.interfaceCount})
                        </Card.Header>
                        <Card.Body>
                          {metrics.network.interfaces.length === 0 ? (
                            <p className="text-muted">No external network interfaces found</p>
                          ) : (
                            metrics.network.interfaces.map((iface, index) => (
                              <div key={index} className="mb-3 p-2 border rounded">
                                <div className="metric-item">
                                  <strong>Interface:</strong> {iface.interface}
                                </div>
                                <div className="metric-item">
                                  <strong>Address:</strong> {iface.address}
                                </div>
                                <div className="metric-item">
                                  <strong>Netmask:</strong> {iface.netmask}
                                </div>
                                <div className="metric-item">
                                  <strong>MAC:</strong> {iface.mac}
                                </div>
                              </div>
                            ))
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Tab>

                {/* Garbage Collection Tab */}
                <Tab eventKey="gc" title="Garbage Collection">
                  <Row className="g-4">
                    <Col md={12}>
                      <Card>
                        <Card.Header>
                          <i className="bi bi-trash me-2"></i>
                          Garbage Collection Statistics
                        </Card.Header>
                        <Card.Body>
                          {metrics.performance.gcStats.available ? (
                            <>
                              <div className="metric-item">
                                <strong>GC Available:</strong> 
                                <Badge bg="success" className="ms-2">Yes</Badge>
                              </div>
                              {metrics.performance.gcStats.gcPerformed ? (
                                <>
                                  <div className="metric-item">
                                    <strong>Last GC Time:</strong> {metrics.performance.gcStats.lastGCTime}ms
                                  </div>
                                  <div className="metric-item">
                                    <strong>Heap Before GC:</strong> {metrics.performance.gcStats.heapBeforeGC}MB
                                  </div>
                                  <div className="metric-item">
                                    <strong>Heap After GC:</strong> {metrics.performance.gcStats.heapAfterGC}MB
                                  </div>
                                  <div className="metric-item">
                                    <strong>Memory Freed:</strong> 
                                    <Badge bg="success" className="ms-2">
                                      {metrics.performance.gcStats.freedMemory}MB
                                    </Badge>
                                  </div>
                                </>
                              ) : (
                                <Alert variant="info">
                                  GC is available but was not performed during metrics collection.
                                  {metrics.performance.gcStats.error && (
                                    <div className="mt-2">
                                      <strong>Error:</strong> {metrics.performance.gcStats.error}
                                    </div>
                                  )}
                                </Alert>
                              )}
                            </>
                          ) : (
                            <Alert variant="warning">
                              Garbage collection is not available. Start Node.js with --expose-gc flag to enable GC monitoring.
                            </Alert>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Tab>

                {/* Database Activity Tab */}
                <Tab eventKey="database" title="Database Activity">
                  <Row className="g-4">
                    {/* Active Queries */}
                    <Col md={12}>
                      <Card>
                        <Card.Header>
                          <i className="bi bi-list-task me-2"></i>
                          Active Database Queries ({dbActivity.totalActive || 0})
                        </Card.Header>
                        <Card.Body>
                          {(!dbActivity.activities || dbActivity.activities.length === 0) ? (
                            <p className="text-muted">No active queries</p>
                          ) : (
                            <div className="table-responsive">
                              <table className="table table-sm">
                                <thead>
                                  <tr>
                                    <th>PID</th>
                                    <th>State</th>
                                    <th>Duration</th>
                                    <th>Client</th>
                                    <th>App</th>
                                    <th>Query Preview</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {dbActivity.activities.map((activity, index) => (
                                    <tr key={index}>
                                      <td>{activity.pid}</td>
                                      <td>
                                        <Badge bg={activity.state === 'active' ? 'success' : 'warning'}>
                                          {activity.state}
                                        </Badge>
                                      </td>
                                      <td>{Math.round(activity.duration_seconds)}s</td>
                                      <td>{activity.client_addr || 'Local'}</td>
                                      <td>{activity.application_name || 'N/A'}</td>
                                      <td>
                                        <code className="small">{activity.query_preview || 'N/A'}</code>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>

                    {/* Slow Queries */}
                    {dbActivity.slowQueries && dbActivity.slowQueries.length > 0 && (
                      <Col md={12}>
                        <Card>
                          <Card.Header className="bg-warning">
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            Slow Queries (&gt;5s) - {dbActivity.slowQueryCount}
                          </Card.Header>
                          <Card.Body>
                            <div className="table-responsive">
                              <table className="table table-sm">
                                <thead>
                                  <tr>
                                    <th>PID</th>
                                    <th>Duration</th>
                                    <th>Started</th>
                                    <th>Query Preview</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {dbActivity.slowQueries.map((query, index) => (
                                    <tr key={index}>
                                      <td>{query.pid}</td>
                                      <td>
                                        <Badge bg="danger">
                                          {Math.round(query.duration_seconds)}s
                                        </Badge>
                                      </td>
                                      <td>{new Date(query.query_start).toLocaleTimeString()}</td>
                                      <td>
                                        <code className="small">{query.query_preview}</code>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    )}
                  </Row>
                </Tab>
              </Tabs>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ServerHealthMonitor; 